import { AppState, AppStateStatus, Linking, Platform } from 'react-native'
import { Redirect, Route, Stack, router, useGlobalSearchParams, usePathname, useSegments } from 'expo-router'
import { observer } from 'mobx-react-lite'
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'

import { useStore } from '@/models'
import { GameBottomModalProvider } from '@/screens/new-design/hooks/useGameBottomModals'

import { PropsWithChildren, useCallback, useEffect } from 'react'
import { useAppVersion } from '../../app/utils/checkStoreVersion/checkAppVersionThenPromptToUpdate'
import { useNetworkDetection } from '@/models/network/useNetworkDetection.hook'
import { DrawerComponent } from '@/components/Drawer/Drawer'
import { useCheatSheet } from '@/components/CheatSheet/useCheatSheet'
import { ShareSheetWeb, useShareSheet } from '@/components/ShareSheet'
import { CheatSheet } from '@/components/CheatSheet'
import { WalkthroughType } from '@/models/game/walkthrough'
import { gameRoutes } from '@/hooks/useIsInGame'
import { handleDeepLink, stashRouteInterruptedByOnboarding } from '@/models/linking'
import { LoaderScreen } from '@/components/LoaderScreen/LoaderScreen'
import { ConvexProvider } from '@/providers/ConvexProvider'
import { useBadgeUnlockToasts } from '@/hooks/convex/useBadgeUnlockToasts'
import { extendLogger, LoggerContexts } from '../../app/utils/logger'
import { useSupportFormModal } from '../../app/components/Support/useSupportFormModal'
import { SupportFormSheet } from '../../app/components/Support/SupportFormSheet'
import { useSessionStore } from '@/models/session/useSessionStore'
import { shouldBlockUiForMeLoad } from '@/models/session/me-loading-gate'
import { GameContainerContext } from '../../app/screens/GameContainer/GameContext'
import { TournamentCountdownToasts } from '../../app/hooks/useTournamentCountdownToasts'
import { AvatarPreferencesHydrator } from '@/screens/profile-screen/edit/AvatarModule'

const logger = extendLogger(LoggerContexts.RootNavigator) // Re-using old logger name

const useAppRequireUpdate = () => {
  const { needsUpdate, showUpdateAlert } = useAppVersion()

  const onChange = useCallback(
    (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active' && needsUpdate) {
        logger.info('onChange', `app activated, needsUpdate is ${needsUpdate}`)
        showUpdateAlert()
      }
    },
    [showUpdateAlert, needsUpdate]
  )

  useEffect(() => {
    logger.info('useEffect[needsUpdate]', `needsUpdate is ${needsUpdate}`)
    if (needsUpdate) {
      showUpdateAlert()
    }
  }, [needsUpdate, showUpdateAlert])

  useEffect(() => {
    logger.info('useEffect[onChange]', 'add listener')
    const appStateListener = AppState.addEventListener('change', onChange)
    return () => {
      appStateListener.remove()
    }
  }, [onChange])

  return
}

const ConvexProviderGameWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <ConvexProvider>
      <AvatarPreferencesHydrator />
      <GameContainerContext.Provider value={{ gameId: undefined, userId: '' }}>
        <TournamentCountdownToasts />
        {children}
      </GameContainerContext.Provider>
    </ConvexProvider>
  )
}

/** Watches badge progress and toasts newly earned tiers. Needs ConvexProvider above. */
const BadgeUnlockToasts = () => {
  useBadgeUnlockToasts()
  return null
}

//#region Root Layout
export const RootLayout = observer(({ children }: PropsWithChildren) => {
  const { ref: cheatSheetModalRef } = useCheatSheet()
  const { ref: supportFormModalRef } = useSupportFormModal()

  return (
    <ConvexProviderGameWrapper>
      <GameBottomModalProvider>
        <BottomSheetModalProvider>
          <CheatSheet ref={cheatSheetModalRef} />
          <SupportFormSheet ref={supportFormModalRef} />
          {children}
          <BadgeUnlockToasts />
        </BottomSheetModalProvider>
      </GameBottomModalProvider>
    </ConvexProviderGameWrapper>
  )
})

// #region Redirect Detection
type RedirectDetectionCallback<T extends Route> = (params: {
  store: ReturnType<typeof useStore>
  pathname: string
}) => (T extends Route ? T : never) | undefined

const onboardingRedirectDetection: RedirectDetectionCallback<
  Extract<Route, '/onboarding/change-name' | '/onboarding/create-avatar'>
> = ({ store, pathname }) => {
  const { isMeFetching, isUserInfoLoaded, userInfo, user } = store.session
  const hasFinishOnboarding = userInfo?.isUserNew === true
  const isPathnameOnboarding = pathname.startsWith('/onboarding')

  // const skipOnboarding = false

  if (hasFinishOnboarding || isPathnameOnboarding || isMeFetching || !isUserInfoLoaded) {
    return
  }

  const hasSetName = !!user?.name && !user.name.includes('placeholder')
  return hasSetName ? '/onboarding/create-avatar' : '/onboarding/change-name'
}

type GameRedirection = Extract<
  Route,
  '/game' | '/game/table-tour' | '/game/my-first-game' | '/game/deal-the-flop' | '/game/raise-or-fold'
>

const gameRedirectDetection: RedirectDetectionCallback<GameRedirection> = ({ store, pathname }) => {
  const { isMyFirstGame, game, walkthroughType } = store.game
  const wasAnGameActive = isMyFirstGame || game?.gameId || walkthroughType

  if (!wasAnGameActive) {
    return
  }

  // In game route, skip
  if (gameRoutes.some((route) => route === pathname) || pathname.startsWith('/admin')) {
    return
  }

  // if (isMyFirstGame) {
  //   return '/game/my-first-game'
  // }

  // if (walkthroughType) {
  //   const walkthroughTypesMapping: Record<WalkthroughType, GameRedirection> = {
  //     TableTour: '/game/table-tour',
  //     DealTheFlop: '/game/deal-the-flop',
  //     RaiseOrFold: '/game/raise-or-fold'
  //   }
  //   if (walkthroughTypesMapping[walkthroughType]) {
  //     return walkthroughTypesMapping[walkthroughType]
  //   }
  // }

  if (game?.gameId) {
    return '/game'
  }

  return
}

// Should be checked in order
const globalRedirects = [onboardingRedirectDetection, gameRedirectDetection]

const useGetInitialRouteRedirection = () => {
  const pathname = usePathname()
  console.log('LOG: useGetInitialRouteRedirection > pathname:', pathname)
  const store = useStore()

  let redirectTo: Route | undefined
  for (let index = 0; index < globalRedirects.length; index++) {
    const detected = globalRedirects[index]({ store, pathname })
    if (detected) {
      redirectTo = detected
      break
    }
  }

  return redirectTo
}
//#endregion

//#region Protected Stack

/** Where a plain sign-up lands. Any other screen before onboarding was reached through a link. */
const TAB_PATHS = new Set(['/', '/home', '/play', '/read', '/learning', '/membership', '/profile'])

export const unstable_settings = {
  // Ensure any route can link back to `/`
  // NOTE: important for screen modals deep linked
  initialRouteName: '(tabs)'
}

const ProtectedStack = observer(() => {
  const store = useStore()

  const pathname = usePathname()
  const params = useGlobalSearchParams()
  const segments = useSegments()

  const trackScreen = useCallback(() => {
    if (pathname !== '') {
      store.analytics.setEvent('Screen View', { pathname, params })
    }
  }, [pathname, params, store.analytics])

  // Track the location in analytics
  useEffect(() => {
    trackScreen()
  }, [trackScreen])

  const enableDefaultNotification = useCallback(async () => {
    store.session.setIsLoggedInFirstTime(false)
    await store.game.enableDefaultNotificationSchedule()
    const pushConfig = {
      dailyPokerish: false,
      gamePlay: true
    }
    await store.session.updateUserPushNotificationConfig({ ...pushConfig })
  }, [store.session, store.game])

  useEffect(() => {
    console.log('isLoggedInFirstTime', store.session.isLoggedInFirstTime)
    if (store.session.isLoggedInFirstTime) {
      //enable friday default notification
      enableDefaultNotification()
    }
  }, [store.session.isLoggedInFirstTime, enableDefaultNotification])

  useEffect(() => {
    store.session.updateAppVersion()
  }, [store.session.updateAppVersion])

  const redirectTo = useGetInitialRouteRedirection()
  const isLoggedIn = useSessionStore((state) => state.tokenConfig?.accessToken !== undefined)

  // Navigate imperatively instead of conditionally returning <Redirect/> in place of <Stack/>.
  // Swapping between the two at this position forces the Stack to unmount/remount, and a
  // freshly-remounted Stack falls back to initialRouteName, which can re-trigger this same
  // redirect decision in an infinite loop (reproducible on native/Simulator). Keeping the
  // Stack mounted continuously and redirecting imperatively avoids that entirely.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on a new redirect target; pathname/params are read at that moment
  useEffect(() => {
    console.log('***RUNNING REDIRECT TO ')
    if (redirectTo) {
      console.log('LOG: > ProtectedStack > redirectTo:', redirectTo)
      const isLandedFromLink = !TAB_PATHS.has(pathname)
      const deferredHref =
        redirectTo.startsWith('/onboarding') && isLandedFromLink
          ? stashRouteInterruptedByOnboarding(pathname, params, segments)
          : undefined
      if (deferredHref) {
        store.analytics.setEvent('Deep Link Deferred', { url: deferredHref, reason: 'onboarding' })
      }
      router.replace(redirectTo)
    }
  }, [redirectTo])

  if (shouldBlockUiForMeLoad({ isLoggedIn, isMeLoading: store.session.isMeLoading, hasUser: !!store.session.user })) {
    return <LoaderScreen />
  }

  return (
    <DrawerComponent>
      <Stack
        initialRouteName="(tabs)"
        screenOptions={{
          headerShown: false,
          gestureEnabled: false,
          freezeOnBlur: true
        }}
      >
        <Stack.Screen
          name="lobby/qr-code"
          options={{
            presentation: 'modal',
            gestureEnabled: true
          }}
        />
        <Stack.Screen
          name="game/create/new"
          options={{
            presentation: 'card',
            animation: 'default',
            gestureEnabled: true
          }}
        />
        <Stack.Screen
          name="game/create/newVariant"
          options={{
            presentation: 'transparentModal',
            animation: 'none',
            gestureEnabled: true
          }}
        />
        <Stack.Screen
          name="game/create/practice"
          options={{
            presentation: 'card',
            animation: 'default',
            gestureEnabled: true
          }}
        />

        {/* JOIN */}
        <Stack.Screen
          name="game/join-live/index"
          options={{
            presentation: 'transparentModal',
            animation: 'none',
            gestureEnabled: true
          }}
        />
        <Stack.Screen
          name="game/join/index"
          options={{
            presentation: 'transparentModal',
            animation: 'none',
            gestureEnabled: true
          }}
        />
        <Stack.Screen
          name="game/join/[code]"
          options={{
            presentation: 'transparentModal',
            animation: 'none',
            gestureEnabled: true
          }}
        />
        <Stack.Screen
          name="support"
          options={{
            presentation: 'transparentModal',
            animation: 'none',
            gestureEnabled: true
          }}
        />

        {/* COMPLETE THE HAND */}
        <Stack.Screen
          name="cth"
          options={{
            presentation: 'card',
            animation: 'default',
            headerShown: false,
            gestureEnabled: true
          }}
        />
      </Stack>
    </DrawerComponent>
  )
})

//#region default _Layout
export default observer(() => {
  useAppRequireUpdate()

  const { ref: shareSheetModalRef } = useShareSheet()
  const store = useStore()

  const isAuthenticated = useSessionStore((state) => state.tokenConfig?.accessToken !== undefined)

  useEffect(() => {
    if (isAuthenticated && Platform.OS === 'web') return

    Linking.getInitialURL()
      .then((initialUrl) => {
        handleDeepLink({
          raw: initialUrl,
          source: isAuthenticated ? 'ColdStartAuthenticated' : 'ColdStartUnauthenticated',
          track: (event, payload) => {
            if (Platform.OS !== 'web') store.analytics.setEvent(event, payload)
          }
        })
      })
      .catch((error) => logger.error('getInitialURL', error))
  }, [isAuthenticated, store.analytics])

  if (!isAuthenticated) {
    return <Redirect href="/login" />
  }

  return (
    <RootLayout>
      <ShareSheetWeb ref={shareSheetModalRef} />
      <ProtectedStack />
    </RootLayout>
  )
})
